import { useState, useEffect } from "react";
import {
  Box, Typography, Paper, Stack, Button, Alert, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, CircularProgress,
} from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import { diagnosticsApi, type DxCollectionPlanItem } from "@/api/diagnostics";
import { errorMessage } from "@/api/client";
import { DiagnosticOrderIdField } from "@/components/DiagnosticOrderIdField";

export function SampleCollectionPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [orderId, setOrderId] = useState(params.get("orderId") ?? "");
  const [active, setActive] = useState(orderId);
  useEffect(() => { const q = params.get("orderId"); if (q) { setOrderId(q); setActive(q); } }, [params]);

  const plan = useQuery({ queryKey: ["dx-collection-plan", active], queryFn: () => diagnosticsApi.collectionPlan(active), enabled: !!active });

  const collect = useMutation({
    meta: { successMessage: "Sample collected" },
    mutationFn: (item: DxCollectionPlanItem) =>
      diagnosticsApi.collectSample({ diagnosticOrderId: active, specimenId: item.specimenId, containerId: item.containerId! }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dx-collection-plan", active] }),
  });

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>Diagnostics · Sample Collection</Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2}>
          <DiagnosticOrderIdField value={orderId} onChange={setOrderId} required sx={{ flexGrow: 1 }} />
          <Button variant="contained" onClick={() => setActive(orderId.trim())} disabled={!orderId.trim()}>Load plan</Button>
        </Stack>
      </Paper>

      {plan.isError && <Alert severity="error">{errorMessage(plan.error)}</Alert>}
      {collect.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(collect.error)}</Alert>}
      {plan.isLoading && active && <CircularProgress />}

      {plan.data && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Accession {plan.data.accessionNumber} — required specimens
          </Typography>
          {plan.data.items.length === 0 && (
            <Alert severity="info">No specimens to collect. Map a default specimen to the order's lab tests on the Lab Tests screen.</Alert>
          )}
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Specimen</TableCell><TableCell>Container</TableCell><TableCell>Tests</TableCell><TableCell>Status</TableCell><TableCell align="right">Action</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {plan.data.items.map(item => (
                <TableRow key={item.specimenId}>
                  <TableCell>{item.specimenCode} — {item.specimenName}</TableCell>
                  <TableCell>{item.containerName ?? <Chip size="small" color="warning" label="no default container" />}</TableCell>
                  <TableCell>{item.tests.join(", ")}</TableCell>
                  <TableCell>{item.alreadyCollected ? <Chip size="small" color="success" label="Collected" /> : <Chip size="small" label="Pending" variant="outlined" />}</TableCell>
                  <TableCell align="right">
                    {item.alreadyCollected
                      ? <Button size="small" variant="outlined" onClick={() => navigate(`/diagnostics/barcode?value=${encodeURIComponent(plan.data!.accessionNumber)}&sub=${item.sampleId?.slice(0, 8) ?? ""}`)}>Barcode</Button>
                      : <Button size="small" variant="contained" disabled={!item.containerId || collect.isPending} onClick={() => collect.mutate(item)}>Collect</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
